import React, { useState, useEffect, useCallback } from 'react';
import { useNavigation } from '../navigation';
import BillingTable, { BillingRow } from './billing-tabel';
import BillingSalesInfo from './billingsales-info';
import BillingSummary from './Billing-summary';
import Navblock from './Navblock';
import PatientsPrescription from './Patients-Prescription';
import PrescriptionDetails from './Prescription-details';

const BillingPage: React.FC = () => {
  const { navigateTo } = useNavigation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [notification, setNotification] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({
    show: false,
    message: '',
    type: 'success'
  });
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorPopupMessage, setErrorPopupMessage] = useState('');
  const [billingRows, setBillingRows] = useState<BillingRow[]>([]);
  const [salesInfo, setSalesInfo] = useState({
    customerName: '',
    phoneNumber: '',
    paymentMode: '',
    amountPaid: 0,
    patientId: undefined as number | undefined
  });
  const [globalDiscount, setGlobalDiscount] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedPrescription, setSelectedPrescription] = useState<{
    doctorId: number;
    clinicId: number;
    appointmentId: string | number;
  } | null>(null);
  const [medicineToAdd, setMedicineToAdd] = useState<Partial<BillingRow> | null>(null);
  const [isPrescriptionExpanded, setIsPrescriptionExpanded] = useState(false);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubmitSuccess = (message: string) => {
    setNotification({
      show: true,
      message: message,
      type: 'success'
    });
  };

  const handleSubmitError = (message: string) => {
    const codeAndMessageMatch = message.match(/^\[([A-Z0-9_]+)\]\s*(.+)$/s);
    const formattedMessage = codeAndMessageMatch
      ? `Code: ${codeAndMessageMatch[1]}\nMessage: ${codeAndMessageMatch[2]}`
      : message;

    setErrorPopupMessage(formattedMessage);
    setShowErrorPopup(true);
    // Auto-hide after 8 seconds
    setTimeout(() => {
      setShowErrorPopup(false);
    }, 8000);
  };

  const handleDone = () => {
    // Reset all state and refresh components
    setBillingRows([]);
    setGlobalDiscount(0);
    setSalesInfo({
      customerName: '',
      phoneNumber: '',
      paymentMode: '',
      amountPaid: 0,
      patientId: undefined
    });
    // Increment refresh key to force remount of child components
    setRefreshKey(prev => prev + 1);
  };

  // Auto-hide notification after 5 seconds
  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification(prev => ({ ...prev, show: false }));
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification.show]);

  // Handle move to bill callback
  const handleMoveToBill = useCallback((medicineData: any) => {
    setMedicineToAdd(medicineData);
    // Reset after a short delay to allow the effect to trigger
    setTimeout(() => {
      setMedicineToAdd(null);
    }, 100);
  }, []);

  // Calculate grand total (same logic as BillingSummary)
  const calculateGrandTotal = useCallback(() => {
    // Calculate subtotal (sum of all line totals)
    const subtotal = billingRows.reduce((sum, row) => sum + (row['Total'] || 0), 0);
    
    // Calculate whole bill discount amount
    const wholeBillDiscountAmount = (subtotal * globalDiscount) / 100;
    
    // Calculate grand total after whole bill discount
    const grandTotal = subtotal - wholeBillDiscountAmount;
    
    return grandTotal;
  }, [billingRows, globalDiscount]);

  const grandTotal = calculateGrandTotal();

  // Handle prescription selection callback
  const handlePrescriptionSelect = useCallback((prescription: any) => {
    // Extract IDs from prescription data
    // Default to the values from the original API call if not available
    const doctorId = prescription.doctor_id || prescription.doctorId || 27;
    const clinicId = prescription.clinic_id || prescription.clinicId || 1;
    const appointmentId = prescription.appointment_id || prescription.appointmentId || prescription.id || prescription.prescription_id || "112";
    
    setSelectedPrescription({
      doctorId,
      clinicId,
      appointmentId
    });
  }, []);

  // Handle rows change callback
  const handleRowsChange = useCallback((rows: BillingRow[]) => {
    setBillingRows(rows);
  }, []);

  // Handle sales data change callback
  const handleSalesDataChange = useCallback((data: {
    customerName: string;
    phoneNumber: string;
    paymentMode: string;
    amountPaid: number;
    patientId?: number;
  }) => {
    setSalesInfo({
      customerName: data.customerName,
      phoneNumber: data.phoneNumber,
      paymentMode: data.paymentMode,
      amountPaid: data.amountPaid,
      patientId: data.patientId
    });
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      width: '100%',
      overflowX: 'hidden',
      boxSizing: 'border-box'
    }}>
      {/* Navblock Header */}
      <Navblock 
        heading="Billing"
        subtitle="Process sales and generate bills"
        showRightButton={false}
      />

      {/* Main Content */}
      <main style={{
        width: '100%',
        maxWidth: '100%',
        margin: '0 auto',
        padding: '0',
        boxSizing: 'border-box',
        overflowX: 'hidden'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '0',
          boxSizing: 'border-box',
          overflowX: 'hidden'
        }}>
          {/* Patients Prescriptions Section - Collapsible */}
          <div 
            onClick={() => setIsPrescriptionExpanded(!isPrescriptionExpanded)}
            style={{
              width: '100%',
              maxWidth: '100%',
              background: 'white',
              borderRadius: '0',
              padding: isPrescriptionExpanded 
                ? (isMobile ? '1rem' : '1.5rem')
                : (isMobile ? '0.75rem 1rem' : '1rem 1.5rem'),
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
              borderBottom: '1px solid #e5e7eb',
              boxSizing: 'border-box',
              overflowX: 'hidden',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              borderLeft: isPrescriptionExpanded ? 'none' : '4px solid #3b82f6'
            }}
            onMouseEnter={(e) => {
              if (!isPrescriptionExpanded) {
                (e.currentTarget as HTMLDivElement).style.background = '#f8fafc';
                (e.currentTarget as HTMLDivElement).style.borderLeftColor = '#2563eb';
              }
            }}
            onMouseLeave={(e) => {
              if (!isPrescriptionExpanded) {
                (e.currentTarget as HTMLDivElement).style.background = 'white';
                (e.currentTarget as HTMLDivElement).style.borderLeftColor = '#3b82f6';
              }
            }}
          >
            {!isPrescriptionExpanded ? (
              /* Collapsed/Minimal View */
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="16" rx="2"/>
                    <path d="M7 8h10"/>
                    <path d="M7 12h6"/>
                  </svg>
                  <div>
                    <div style={{
                      fontSize: isMobile ? '0.9375rem' : '1rem',
                      fontWeight: 600,
                      color: '#111827',
                      marginBottom: '0.125rem'
                    }}>
                      Patients Prescriptions
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#6b7280'
                    }}>
                      Click to view prescriptions
                    </div>
                  </div>
                </div>
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="#6b7280" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  style={{
                    transform: 'rotate(0deg)',
                    transition: 'transform 0.3s ease'
                  }}
                >
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </div>
            ) : (
              /* Expanded View */
              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1rem',
                  paddingBottom: '0.75rem',
                  borderBottom: '1px solid #e5e7eb'
                }}>
                  <div>
                    <h2 style={{
                      fontSize: isMobile ? '1.1rem' : '1.25rem',
                      fontWeight: 700,
                      color: '#111827',
                      margin: 0,
                      marginBottom: '0.25rem'
                    }}>
                      Patients Prescriptions
                    </h2>
                    <p style={{
                      margin: 0,
                      fontSize: '0.8rem',
                      color: '#6b7280'
                    }}>
                      List of recent prescriptions from your pharmacy
                    </p>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsPrescriptionExpanded(false);
                      }}
                      style={{
                        background: 'transparent',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        padding: '0.5rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.background = '#f3f4f6';
                        (e.currentTarget as HTMLButtonElement).style.borderColor = '#9ca3af';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                        (e.currentTarget as HTMLButtonElement).style.borderColor = '#d1d5db';
                      }}
                      title="Collapse"
                    >
                      <svg 
                        width="18" 
                        height="18" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="#6b7280" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      >
                        <polyline points="18 15 12 9 6 15"/>
                      </svg>
                    </button>
                  </div>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <PatientsPrescription 
                    hideHeader={true}
                    onPrescriptionSelect={handlePrescriptionSelect}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Prescription Details Section - Only show when a prescription is selected */}
          {selectedPrescription && (
            <div style={{
              width: '100%',
              maxWidth: '100%',
              background: 'white',
              borderRadius: '0',
              paddingTop: isMobile ? '1rem' : '1.5rem',
              paddingRight: isMobile ? '1rem' : '1.5rem',
              paddingBottom: isMobile ? '1rem' : '1.5rem',
              paddingLeft: isMobile ? '1rem' : '1.5rem',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
              borderBottom: '1px solid #e5e7eb',
              boxSizing: 'border-box',
              overflowX: 'hidden'
            }}>
              <PrescriptionDetails 
                doctorId={selectedPrescription.doctorId}
                clinicId={selectedPrescription.clinicId}
                appointmentId={selectedPrescription.appointmentId}
                onMoveToBill={handleMoveToBill}
              />
            </div>
          )}

          {/* Billing Table */}
          <div key={`billing-table-${refreshKey}`} style={{
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            background: 'white',
            borderRadius: '0',
            paddingTop: isMobile ? '1rem' : '2rem',
            paddingRight: '0',
            paddingBottom: isMobile ? '1rem' : '2rem',
            paddingLeft: '0',
            boxShadow: isMobile ? '0 2px 4px rgba(0, 0, 0, 0.08)' : '0 4px 6px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.1)',
            marginTop: '0',
            marginBottom: '0',
            marginLeft: '0',
            marginRight: '0',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            alignItems: isMobile ? 'center' : 'stretch',
            justifyContent: 'center'
          }}>
            <BillingTable 
              onSubmitSuccess={handleSubmitSuccess}
              onSubmitError={handleSubmitError}
              onRowsChange={handleRowsChange}
              globalDiscount={globalDiscount}
              onGlobalDiscountChange={setGlobalDiscount}
              addMedicineData={medicineToAdd}
            />
          </div>

          {/* Sales Information */}
          <div key={`sales-info-${refreshKey}`} style={{
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflowX: 'hidden'
          }}>
            <BillingSalesInfo 
              grandTotal={grandTotal}
              onDataChange={handleSalesDataChange}
            />
          </div>

          {/* Billing Summary */}
          <div style={{
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflowX: 'hidden'
          }}>
            <BillingSummary
              key={`billing-summary-${refreshKey}`}
              rows={billingRows}
              customerName={salesInfo.customerName}
              phoneNumber={salesInfo.phoneNumber}
              paymentMode={salesInfo.paymentMode}
              amountPaid={salesInfo.amountPaid}
              patientId={salesInfo.patientId}
              globalDiscount={globalDiscount}
              onGlobalDiscountChange={setGlobalDiscount}
              onSubmitSuccess={(message: string) => {
                setNotification({
                  show: true,
                  message: message,
                  type: 'success'
                });
              }}
              onSubmitError={(message: string) => {
                setNotification({
                  show: true,
                  message: message,
                  type: 'error'
                });
              }}
              onDone={handleDone}
            />
          </div>
        </div>
      </main>

      {/* Success Notification Toast */}
      {notification.show && notification.type === 'success' && (
        <div
          style={{
            position: 'fixed',
            top: isMobile ? '10px' : '20px',
            right: isMobile ? '10px' : '20px',
            left: isMobile ? '10px' : 'auto',
            zIndex: 10000,
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            padding: isMobile ? '0.875rem 1rem' : '1rem 1.5rem',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '0.5rem' : '0.75rem',
            minWidth: isMobile ? 'auto' : '300px',
            maxWidth: isMobile ? '100%' : '500px',
            width: isMobile ? 'calc(100% - 20px)' : 'auto',
            animation: 'slideInRight 0.3s ease-out',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}
          onClick={() => setNotification(prev => ({ ...prev, show: false }))}
        >
          <div style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <div style={{ 
            flex: 1, 
            fontSize: isMobile ? '0.875rem' : '0.9375rem', 
            fontWeight: '500',
            lineHeight: '1.4'
          }}>
            {notification.message}
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setNotification(prev => ({ ...prev, show: false }));
            }}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '6px',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'background 0.2s ease',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              }
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
          <style>{`
            @keyframes slideInRight {
              from {
                transform: translateX(100%);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }
          `}</style>
        </div>
      )}

      {/* Error Notification Toast */}
      {notification.show && notification.type === 'error' && (
        <div
          style={{
            position: 'fixed',
            top: isMobile ? '10px' : '20px',
            right: isMobile ? '10px' : '20px',
            left: isMobile ? '10px' : 'auto',
            zIndex: 10000,
            background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
            color: '#991b1b',
            padding: isMobile ? '0.875rem 1rem' : '1rem 1.5rem',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(239, 68, 68, 0.25)',
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '0.5rem' : '0.75rem',
            minWidth: isMobile ? 'auto' : '300px',
            maxWidth: isMobile ? 'calc(100% - 20px)' : '500px',
            width: isMobile ? 'calc(100% - 20px)' : 'auto',
            animation: 'slideInRight 0.3s ease-out',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            border: '2px solid #ef4444',
          }}
          onClick={() => setNotification(prev => ({ ...prev, show: false }))}
        >
          <div style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            background: 'rgba(153, 27, 27, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 9v4M12 17h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"/>
            </svg>
          </div>
          <div style={{
            flex: 1,
            fontSize: isMobile ? '0.875rem' : '0.9375rem',
            fontWeight: '700',
            lineHeight: '1.4',
            whiteSpace: 'pre-wrap'
          }}>
            {notification.message}
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setNotification(prev => ({ ...prev, show: false }));
            }}
            style={{
              background: 'rgba(153, 27, 27, 0.12)',
              border: 'none',
              borderRadius: '6px',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'background 0.2s ease',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.background = 'rgba(153, 27, 27, 0.18)';
              }
            }}
            onMouseLeave={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.background = 'rgba(153, 27, 27, 0.12)';
              }
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
          <style>{`
            @keyframes slideInRight {
              from {
                transform: translateX(100%);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }
          `}</style>
        </div>
      )}

      {/* Error Popup */}
      {showErrorPopup && (
        <div
          style={{
            position: 'fixed',
            top: isMobile ? '10px' : '20px',
            right: isMobile ? '10px' : '20px',
            left: isMobile ? '10px' : 'auto',
            zIndex: 10000,
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
            border: '2px solid #f59e0b',
            color: '#92400e',
            padding: isMobile ? '0.875rem 1rem' : '1rem 1.5rem',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(245, 158, 11, 0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '0.5rem' : '0.75rem',
            minWidth: isMobile ? 'auto' : '350px',
            maxWidth: isMobile ? 'calc(100% - 20px)' : '500px',
            width: isMobile ? 'calc(100% - 20px)' : 'auto',
            animation: 'slideInRight 0.3s ease-out',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}
          onClick={() => setShowErrorPopup(false)}
        >
          <div style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            background: 'rgba(245, 158, 11, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 9v4M12 17h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"/>
            </svg>
          </div>
          <div style={{ 
            flex: 1, 
            fontSize: isMobile ? '0.875rem' : '0.9375rem', 
            fontWeight: '600',
            lineHeight: '1.4',
            whiteSpace: 'pre-wrap'
          }}>
            {errorPopupMessage}
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowErrorPopup(false);
            }}
            style={{
              background: 'rgba(245, 158, 11, 0.2)',
              border: 'none',
              borderRadius: '6px',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'background 0.2s ease',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.background = 'rgba(245, 158, 11, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.background = 'rgba(245, 158, 11, 0.2)';
              }
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default BillingPage;


